import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ManagementOfChangeService } from 'app/modules/management-of-change/services/management-of-change.service';
import { handleFormatDate } from 'app/core/shared/helper/date.helper';
import { Router } from '@angular/router';
import module from 'app/modules/management-of-change/moc.module';
import { EMPTY, map, merge, Subject, switchMap, takeUntil } from 'rxjs';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { DEFAULT_COLUMN_LABELS } from 'app/core/shared/constant/column-label';
import { MatDialog } from '@angular/material/dialog';
import { ManuallyCheckComponent } from 'app/modules/management-of-change/component/manually-check/manually-check.component';
import { MocSearchStateService } from '../../state/search.state';
import { ApplySortPayload } from 'app/core/shared/component/sort-menu/sort-menu.component';

@Component({
  selector: 'app-records',
  templateUrl: './application-tracking.page.html',
  imports: module,
  standalone: true,
  styleUrls: ['./application-tracking.page.scss']
})
export class ApplicationTrackingPage {
  @ViewChild(MatPaginator) private _paginator: MatPaginator;
  @ViewChild(MatSort) private _sort: MatSort;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  searchForm: UntypedFormGroup;
  dt: any
  startingIndex: number = 0
  isLoading: boolean = false
  selectedSortColumn: any[] = []


  sortInfo: string = 'Failure Date';
  private columnLabels: { [key: string]: string } = {
    ...DEFAULT_COLUMN_LABELS,
    wellProductionStatus: 'Status',
    failureDate: 'Failure Date',
    failureEquipment: 'Failure Equipment',
    overdueDays: 'Overdue Day',
    overdueDate: 'Overdue Date',
    submissionStatus: 'MOC Issuance Status',
    submissionDate: 'Submission Date',
  };

  columnSort = Object.entries(this.columnLabels).map(([key, name]) => ({
    key,
    name,
    isChecked: false, // default unchecked
  }));

  constructor(
    private mocService: ManagementOfChangeService,
    private _formBuilder: UntypedFormBuilder,
    public router: Router,
    private _changeDetectorRef: ChangeDetectorRef,
    private dialog: MatDialog,
    private state: MocSearchStateService,
  ) { }

  ngOnInit() {
    this.setSearch();
    this.setDataTable();
    this.setPage({ offset: 0 });
  }

  ngAfterViewInit() {
    if (this._sort && this._paginator) {
      // Set the initial sort
      this._sort.sort({
        id: 'field',
        start: 'asc',
        disableClear: true,
      });

      // Mark for check
      this._changeDetectorRef.markForCheck();

      // If the user changes the sort order...
      this._sort.sortChange
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(() => {
          // Reset back to the first page
          this._paginator.pageIndex = 0;

          this.onSort(this._sort)
        });


      // Get platform types if sort or page changes
      merge(this._sort.sortChange, this._paginator.page).pipe(
        switchMap(() => {
          // this.isLoading = true;

          this.startingIndex = this._paginator.pageIndex * this._paginator.pageSize;

          let pageInfo = {
            'count': this._paginator.length,
            'pageSize': this._paginator.pageSize,
            'limit': this._paginator.pageSize,
            'offset': this._paginator.pageIndex
          }
          this.setPage(pageInfo); // Emit page number

          return EMPTY
        }),
        map(() => {
          this.isLoading = false;
        }),
      ).subscribe();
    }
  }

  handleSearch(event$) {
    const field = event$?.Field;
    const platform = event$?.Platform;
    const well = event$?.Well;
    const string = event$?.String;

    if ([field, platform, well, string].some(v => v != null && v !== '')) {
      this.setPage({ offset: 0 });
    }
  }

  onSort(sort: any) {
    if (!sort) return;
    this.searchForm.patchValue({
      OrderBy1: sort.active,
      OrderDir: sort.direction,
    });

    this.sortInfo = this.columnLabels[sort.active] || sort.active;
    this.setPage({ offset: 0 });
  }

  setSearch() {
    this.sortInfo = 'Failure Date'
    this.searchForm = this._formBuilder.group({
      Page: [0],
      PageSize: [20],
      OrderBy1: [null],
      OrderBy2: [null],
      OrderBy3: [null],
      OrderDir: 'asc',
      WellName: [''],
      PlatformName: [''],
      StringName: [''],
      FieldName: [''],
      SortColumns: this._formBuilder.array(
        this.columnSort.map(col => this._formBuilder.group(col))
      ) // col already has {key, name, isChecked}
    });

    this.searchForm.get('PageSize').valueChanges.subscribe(value => this.setSize(value));
  }

  setSize(size: number) {
    this.dt.page.size = size;
    this.dt.page.offset = 0
    this.setPage({ offset: this.dt.page.offset });
  }

  setPage(pageInfo) {
    const saved = this.state.get()
    let params: any = {}

    // if have saved search
    if (saved) {
      // is search for changes
      const useSaved = !this.searchForm.dirty;

      this.searchForm.patchValue(
        useSaved ? saved : { Page: pageInfo.offset + 1 },
        { emitEvent: !useSaved }
      );

      this.dt.page.offset = pageInfo.offset;
      this.startingIndex = this.dt.page.offset * (useSaved ? saved.PageSize : this.dt.page.size);
      params = useSaved ? saved : this.searchForm.value;

    } else {
      this.dt.page.offset = pageInfo.offset;
      this.searchForm.patchValue({ Page: this.dt.page.offset + 1 });
      this.startingIndex = this.dt.page.offset * this.dt.page.size;
      params = this.searchForm.value;
    }

    const search: any = {
      wellLongName: params.Well?.wellLongName,
      stringShortName: params.String?.stringShortName,
      platformLongName: params.Platform?.platformLongName,
      fieldLongName: params.Field?.fieldLongName,
    };

    console.log(params)
    console.log(search)

    this.mocService
      .getApplicationTracking({ ...params, search })
      .subscribe((pagedData) => {
        this.dt.page.totalPages = pagedData.data.totalPages;
        this.dt.page.totalRecords = pagedData.data.totalRecords;
        this.dt.rows = pagedData.data.data;
      });
  }

  setDataTable() {
    this.dt = {
      page: {
        size: this.searchForm.get('PageSize')?.value,
        totalRecords: 0,
        totalPages: 0,
        offset: 0,
      },
      rows: [],
    };
  }

  reset() {
    this.state.clear(); // clear session storage
    this.setSearch();
    this.setPage({ offset: 0 });
  }

  handleFormatDate(dateString: string, withTime) {
    return handleFormatDate(dateString, withTime)
  }

  view(appTracking) {
    if (this.searchForm.dirty) this.state.set(this.searchForm.value);
    this.router.navigate([`/data-management/management-of-change/application-tracking/${appTracking.id}/view`]);
  }

  manuallyCheck(appTracking) {
    const dialogRef = this.dialog.open(ManuallyCheckComponent, {
      data: appTracking,
      panelClass: 'overscroll-y-none'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result == 'success') {
        this.setPage({ offset: this.dt.page.offset })
      }
    });
  }

  downloadAppTrackingExcel() {
    this.mocService.downloadAppTrackingExcel()
  }

  applySort(payload: ApplySortPayload) {
    // Patch form (fill missing with null)
    const [OrderBy1, OrderBy2, OrderBy3] = [
      payload.columns[0]?.key ?? null,
      payload.columns[1]?.key ?? null,
      payload.columns[2]?.key ?? null,
    ];

    this.searchForm.patchValue({
      OrderBy1,
      OrderBy2,
      OrderBy3,
      OrderDir: payload.orderDir ?? 'asc',
    });

    const labels = payload.columns.map(k => k.name);
    this.sortInfo = labels.length ? labels.join(' → ') : 'No sorting';
    this.setPage({ offset: 0 });
  }
}


